/**
 * Input atom: single-line text field. Styles in Input.styles; no inline styles.
 * Aligns with HTML <input>. Use with <label htmlFor={id}> or aria-label.
 * Pass error for validation message and error styling (aria-invalid set automatically).
 *
 * @param rounded - Border radius. Default "lg".
 * @param error - Message shown below input; applies error border and aria-invalid.
 */

import React from 'react';
import { Typography } from '../Typography';
import type { InputProps } from './Input.types';
import { getInputClasses } from './Input.styles';

export type { InputProps, InputRounded, InputSize } from './Input.types';

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className = '',
      error,
      size = 'md',
      responsive = true,
      rounded = 'lg',
      'aria-invalid': ariaInvalid,
      'aria-describedby': ariaDescribedBy,
      ...props
    },
    ref
  ) => {
    const hasError = Boolean(error);
    const errorId = error ? `${props.id ?? 'input'}-error` : undefined;

    return (
      <div className="relative w-full leading-none">
        <input
          ref={ref}
          aria-invalid={hasError ? true : ariaInvalid}
          aria-describedby={error ? errorId : ariaDescribedBy}
          className={getInputClasses(size, rounded, hasError, className, responsive)}
          {...props}
        />
        {error && (
          <Typography
            as="p"
            id={errorId}
            weight="medium"
            variant="body-xs"
            color="error"
            className="mt-1 leading-normal"
          >
            {error}
          </Typography>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
